import React, { useEffect, useRef, useState } from "react";
import { Header } from "@/components/Header";
import { useApp } from "@/context/AppContext";
import api, { imgUrl, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TouchInput } from "@/components/VirtualKeyboard";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Users, ClipboardList, Tags, Ruler,
  Plus, Pencil, Trash2, Upload, ShoppingBag, Clock, DollarSign, X, GripVertical, Star,
} from "lucide-react";

const TABS = [
  { id: "dashboard", icon: LayoutDashboard, key: "dashboard" },
  { id: "products", icon: Package, key: "products" },
  { id: "orders", icon: ClipboardList, key: "orders" },
  { id: "users", icon: Users, key: "users" },
  { id: "categories", icon: Tags, key: "categories" },
  { id: "sizes", icon: Ruler, key: "sizesTab" },
];

const STATUS_COLORS = {
  pendiente: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  procesado: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  enviado: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
};

export default function Admin() {
  const { t } = useApp();
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 overflow-x-auto kiosk-scroll pb-2 mb-6">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-nav-${tb.id}-tab`}
              onClick={() => setTab(tb.id)}
              className={`min-h-[52px] px-5 rounded-xl border font-display font-semibold whitespace-nowrap flex items-center gap-2 transition-colors ${
                tab === tb.id ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"
              }`}
            >
              <tb.icon className="h-5 w-5" /> {t(tb.key)}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <Dashboard />}
        {tab === "products" && <ProductsAdmin />}
        {tab === "orders" && <OrdersAdmin />}
        {tab === "users" && <UsersAdmin />}
        {tab === "categories" && <CategoriesAdmin />}
        {tab === "sizes" && <SizesAdmin />}
      </div>
    </div>
  );
}

function Dashboard() {
  const { t } = useApp();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const cards = [
    { icon: ShoppingBag, label: t("totalOrders"), value: stats.total_orders, testId: "stat-total-orders" },
    { icon: Clock, label: t("pendingOrders"), value: stats.pending_orders, testId: "stat-pending-orders" },
    { icon: DollarSign, label: t("totalSales"), value: `$${stats.total_sales.toFixed(2)}`, testId: "stat-total-sales" },
    { icon: Package, label: t("products"), value: stats.products_count, testId: "stat-products" },
    { icon: Users, label: t("users"), value: stats.users_count, testId: "stat-users" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testId} className="rounded-2xl border bg-card p-5 shadow-sm animate-fade-up">
          <c.icon className="h-7 w-7 text-primary" />
          <div className="mt-3 font-mono font-extrabold text-3xl">{c.value}</div>
          <div className="text-sm text-muted-foreground">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

function SectionHeader({ title, onAdd, addLabel, testId }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display font-bold text-2xl">{title}</h2>
      {onAdd && (
        <Button data-testid={testId} onClick={onAdd} className="min-h-[48px] rounded-xl gap-2">
          <Plus className="h-5 w-5" /> {addLabel}
        </Button>
      )}
    </div>
  );
}

function ProductsAdmin() {
  const { t, lang } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/products/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const toggleStock = async (p) => {
    await api.patch(`/products/${p.id}/stock`, { out_of_stock: !p.out_of_stock });
    load();
  };

  return (
    <div>
      <SectionHeader
        title={t("products")}
        onAdd={() => setEditing({})}
        addLabel={t("addProduct")}
        testId="admin-add-product-button"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="rounded-2xl border bg-card overflow-hidden shadow-sm">
            <div className="flex gap-3 p-3">
              <div className="h-20 w-20 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm line-clamp-1">{lang === "es" ? p.name_es : p.name_en}</div>
                <div className="font-mono text-primary font-bold">${p.price.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground">
                  {t("stock")}: {p.stock}
                </div>
                {(p.out_of_stock || p.stock <= 0) && (
                  <span className="inline-block mt-1 text-[10px] font-bold uppercase text-destructive">{t("outOfStock")}</span>
                )}
              </div>
            </div>
            <div className="flex border-t divide-x">
              <button data-testid={`admin-product-oos-${p.id}`} onClick={() => toggleStock(p)} className="flex-1 py-2.5 text-xs font-semibold hover:bg-muted">
                {p.out_of_stock ? t("inStock") : t("markOutOfStock")}
              </button>
              <button data-testid={`admin-product-edit-${p.id}`} onClick={() => setEditing(p)} className="flex-1 py-2.5 text-xs font-semibold hover:bg-muted flex items-center justify-center gap-1">
                <Pencil className="h-4 w-4" /> {t("edit")}
              </button>
              <button data-testid={`admin-product-delete-${p.id}`} onClick={() => del(p.id)} className="flex-1 py-2.5 text-xs font-semibold text-destructive hover:bg-muted flex items-center justify-center gap-1">
                <Trash2 className="h-4 w-4" /> {t("delete")}
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <ProductForm
          product={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ProductForm({ product, categories, onClose, onSaved }) {
  const { t } = useApp();
  const isEdit = !!product.id;
  const [form, setForm] = useState({
    name_es: product.name_es || "",
    name_en: product.name_en || "",
    description_es: product.description_es || "",
    description_en: product.description_en || "",
    category_id: product.category_id || categories[0]?.id || "",
    price: product.price ?? 0,
    sizes: (product.sizes || []).join(", "),
    units_per_box: product.units_per_box ?? 0,
    weight_kg: product.weight_kg ?? 0,
    dimensions: product.dimensions || "",
    stock: product.stock ?? 0,
    out_of_stock: product.out_of_stock ?? false,
    images: product.images || [],
  });
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [urlDraft, setUrlDraft] = useState("");
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const moveImage = (from, to) => {
    setForm((f) => {
      const imgs = [...f.images];
      const [m] = imgs.splice(from, 1);
      imgs.splice(to, 0, m);
      return { ...f, images: imgs };
    });
  };

  const addUrl = () => {
    const u = urlDraft.trim();
    if (!u) return;
    set("images", [...form.images, u]);
    setUrlDraft("");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...form.images, data.url]);
      toast.success("OK");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const payload = {
      ...form,
      price: Number(form.price),
      units_per_box: Number(form.units_per_box),
      weight_kg: Number(form.weight_kg),
      stock: Number(form.stock),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isEdit) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const field = (label, k, type = "text") => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      <TouchInput testId={`product-field-${k}`} type={type} value={String(form[k])} onChange={(v) => set(k, v)} className="mt-1" />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto kiosk-scroll" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? t("editProduct") : t("addProduct")}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {field(t("nameEs"), "name_es")}
          {field(t("nameEn"), "name_en")}
          {field(t("descEs"), "description_es")}
          {field(t("descEn"), "description_en")}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("category")}</label>
            <select
              data-testid="product-field-category"
              value={form.category_id}
              onChange={(e) => set("category_id", e.target.value)}
              className="mt-1 w-full min-h-[56px] rounded-xl border bg-card px-4"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name_es}</option>
              ))}
            </select>
          </div>
          {field(t("price"), "price", "number")}
          {field(t("sizes") + " (a,b,c)", "sizes")}
          {field(t("stock"), "stock", "number")}
          {field(t("unitsPerBox"), "units_per_box", "number")}
          {field(t("weight") + " kg", "weight_kg", "number")}
          {field(t("dimensions"), "dimensions")}
        </div>

        <div className="mt-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("uploadPhoto")}</label>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t("photosHint")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            {form.images.map((im, i) => (
              <div
                key={`${im}-${i}`}
                data-testid={`product-image-thumb-${i}`}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIndex !== null && dragIndex !== i) moveImage(dragIndex, i);
                  setDragIndex(null);
                }}
                onDragEnd={() => setDragIndex(null)}
                className={`relative h-24 w-24 rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-opacity ${
                  dragIndex === i ? "opacity-40 border-primary" : "border-border"
                }`}
              >
                <img src={imgUrl(im)} alt="" className="h-full w-full object-cover pointer-events-none" />
                <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded bg-black/55 text-white flex items-center justify-center">
                  <GripVertical className="h-3.5 w-3.5" />
                </div>
                {i === 0 && (
                  <span className="absolute bottom-0 inset-x-0 bg-primary/90 text-primary-foreground text-[9px] font-bold text-center py-0.5 flex items-center justify-center gap-0.5">
                    <Star className="h-2.5 w-2.5 fill-current" /> {t("mainPhoto")}
                  </span>
                )}
                <button
                  type="button"
                  data-testid={`product-image-remove-${i}`}
                  onClick={() => set("images", form.images.filter((_, idx) => idx !== i))}
                  className="absolute top-0.5 right-0.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="product-upload-button"
              onClick={() => fileRef.current?.click()}
              className="h-24 w-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary"
            >
              {uploading ? "..." : <><Upload className="h-5 w-5" /><span className="text-[10px] mt-1">{t("uploadPhoto")}</span></>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
          </div>
          <div className="mt-2 flex gap-2 items-center">
            <TouchInput
              testId="product-image-url"
              value={urlDraft}
              onChange={setUrlDraft}
              placeholder="https://..."
              className="flex-1"
            />
            <Button type="button" variant="outline" onClick={addUrl} data-testid="product-image-url-add" className="min-h-[56px] rounded-xl gap-1">
              <Plus className="h-5 w-5" /> URL
            </Button>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 min-h-[52px] rounded-xl" data-testid="product-form-cancel">
            {t("cancel")}
          </Button>
          <Button onClick={save} className="flex-1 min-h-[52px] rounded-xl font-bold" data-testid="product-form-save">
            {t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrdersAdmin() {
  const { t } = useApp();
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/orders/${id}/status`, { status });
    toast.success(t("saved"));
    load();
  };

  return (
    <div>
      <SectionHeader title={t("orders")} />
      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="rounded-2xl border bg-card p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold text-lg">#{o.order_number}</div>
                <div className="text-sm text-muted-foreground">{o.seller_name} · {new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-xl text-primary">${o.total.toFixed(2)}</span>
                <span data-testid={`admin-order-status-badge-${o.order_number}`} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${STATUS_COLORS[o.status]}`}>
                  {t(o.status)}
                </span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setDetail(o)} data-testid={`admin-order-view-${o.order_number}`} className="rounded-lg">
                {t("viewDetails")}
              </Button>
              {["pendiente", "procesado", "enviado"].map((s) => (
                <button
                  key={s}
                  data-testid={`admin-order-set-${s}-${o.order_number}`}
                  onClick={() => setStatus(o.id, s)}
                  className={`h-9 px-3 rounded-lg text-xs font-semibold border ${o.status === s ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
                >
                  {t(s)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{t("orderDetails")} #{detail.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p><b>{t("seller")}:</b> {detail.seller_name} ({detail.seller_email})</p>
                <p><b>{t("customerInfo")}:</b> {detail.customer_info || "-"}</p>
                <div className="rounded-lg border divide-y mt-2">
                  {detail.items.map((it, i) => (
                    <div key={i} className="flex justify-between px-3 py-2">
                      <span>{it.name} · {it.size} × {it.quantity}</span>
                      <span className="font-mono">${it.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-bold pt-2">
                  <span>{t("total")}</span>
                  <span className="font-mono text-primary">${detail.total.toFixed(2)}</span>
                </div>
                {detail.notes && <p className="pt-2"><b>{t("notes")}:</b> {detail.notes}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersAdmin() {
  const { t } = useApp();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success(t("deleted"));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <SectionHeader title={t("users")} onAdd={() => setEditing({})} addLabel={t("addUser")} testId="admin-add-user-button" />
      <div className="rounded-2xl border bg-card overflow-hidden">
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center justify-between px-4 py-3 border-b last:border-0">
            <div>
              <div className="font-semibold">{u.name}</div>
              <div className="text-sm text-muted-foreground">{u.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-primary/10 text-primary">{u.role}</span>
              <button data-testid={`admin-user-edit-${u.id}`} onClick={() => setEditing(u)} className="h-9 w-9 rounded-lg border flex items-center justify-center hover:bg-muted"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`admin-user-delete-${u.id}`} onClick={() => del(u.id)} className="h-9 w-9 rounded-lg border text-destructive flex items-center justify-center hover:bg-muted"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <UserForm user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function UserForm({ user, onClose, onSaved }) {
  const { t } = useApp();
  const isEdit = !!user.id;
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", password: "", role: user.role || "vendedor" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (isEdit) await api.put(`/users/${user.id}`, { name: form.name, role: form.role, password: form.password || undefined });
      else await api.post("/users", form);
      toast.success(t("saved"));
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? t("edit") : t("addUser")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">{t("name")}</label>
            <TouchInput testId="user-field-name" value={form.name} onChange={(v) => set("name", v)} className="mt-1" />
          </div>
          {!isEdit && (
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">{t("email")}</label>
              <TouchInput testId="user-field-email" value={form.email} onChange={(v) => set("email", v)} className="mt-1" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">{t("password")}</label>
            <TouchInput testId="user-field-password" type="password" value={form.password} onChange={(v) => set("password", v)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">{t("role")}</label>
            <select data-testid="user-field-role" value={form.role} onChange={(e) => set("role", e.target.value)} className="mt-1 w-full min-h-[56px] rounded-xl border bg-card px-4">
              <option value="vendedor">vendedor</option>
              <option value="gerente">gerente</option>
              <option value="admin">admin</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 min-h-[52px] rounded-xl">{t("cancel")}</Button>
          <Button onClick={save} className="flex-1 min-h-[52px] rounded-xl font-bold" data-testid="user-form-save">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesAdmin() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [es, setEs] = useState("");
  const [en, setEn] = useState("");
  const load = () => api.get("/categories").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!es || !en) return;
    await api.post("/categories", { name_es: es, name_en: en });
    setEs(""); setEn(""); load();
  };
  const del = async (id) => { await api.delete(`/categories/${id}`); load(); };

  return (
    <div>
      <SectionHeader title={t("categories")} />
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div><label className="text-xs font-semibold uppercase text-muted-foreground">{t("nameEs")}</label><TouchInput testId="category-input-es" value={es} onChange={setEs} className="mt-1 w-48" /></div>
        <div><label className="text-xs font-semibold uppercase text-muted-foreground">{t("nameEn")}</label><TouchInput testId="category-input-en" value={en} onChange={setEn} className="mt-1 w-48" /></div>
        <Button onClick={add} className="min-h-[56px] rounded-xl gap-2" data-testid="category-add-button"><Plus className="h-5 w-5" /> {t("addCategory")}</Button>
      </div>
      <div className="rounded-2xl border bg-card overflow-hidden">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-4 py-3 border-b last:border-0">
            <span className="font-semibold">{c.name_es} <span className="text-muted-foreground font-normal">/ {c.name_en}</span></span>
            <button data-testid={`category-delete-${c.id}`} onClick={() => del(c.id)} className="h-9 w-9 rounded-lg border text-destructive flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SizesAdmin() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [group, setGroup] = useState("calzado");
  const load = () => api.get("/sizes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!label) return;
    await api.post("/sizes", { label, group });
    setLabel(""); load();
  };
  const del = async (id) => { await api.delete(`/sizes/${id}`); load(); };

  return (
    <div>
      <SectionHeader title={t("sizesTab")} />
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div><label className="text-xs font-semibold uppercase text-muted-foreground">{t("label")}</label><TouchInput testId="size-input-label" value={label} onChange={setLabel} className="mt-1 w-40" /></div>
        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground">{t("group")}</label>
          <select data-testid="size-input-group" value={group} onChange={(e) => setGroup(e.target.value)} className="mt-1 w-40 min-h-[56px] rounded-xl border bg-card px-4">
            <option value="calzado">calzado</option>
            <option value="ropa">ropa</option>
            <option value="general">general</option>
          </select>
        </div>
        <Button onClick={add} className="min-h-[56px] rounded-xl gap-2" data-testid="size-add-button"><Plus className="h-5 w-5" /> {t("addSize")}</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-2 rounded-xl border bg-card px-4 py-2">
            <span className="font-semibold">{s.label}</span>
            <span className="text-xs text-muted-foreground">({s.group})</span>
            <button data-testid={`size-delete-${s.id}`} onClick={() => del(s.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
